<template>
  <AnalyticsCard
    title="Where visitors come from"
    subtitle="Shaded by visitor count"
    :loading="loading"
    :empty="!loading && data.length === 0"
    empty-title="No location data yet"
    empty-hint="Countries fill in as visitors arrive."
    :height="320"
  >
    <div ref="el" class="w-full h-80"></div>
  </AnalyticsCard>
</template>

<script setup>
import { computed, toRef } from 'vue'
import topology from '../../../assets/maps/world.topo.json'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { useAnalyticsChart, PALETTE, hexToRgba } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  data:    { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
})

const max = computed(() => Math.max(1, ...props.data.map(c => c.visitors)))

const { el } = useAnalyticsChart((_node, tokens) => {
  if (!props.data.length) return null

  return {
    __map: true,
    chart: { map: topology, backgroundColor: 'transparent', height: 320, spacing: [8, 8, 8, 8] },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    mapNavigation: { enabled: true, enableMouseWheelZoom: false, buttonOptions: { alignTo: 'spacingBox' } },
    // Scaled to the largest country in the actual data. The old map pinned
    // max to a hardcoded 1000, so an account with fifty visitors rendered
    // every country in the palest possible blue and looked broken.
    colorAxis: {
      min: 1,
      max: max.value,
      type: max.value > 20 ? 'logarithmic' : 'linear',
      minColor: hexToRgba(PALETTE.blue, 0.18),
      maxColor: PALETTE.blue,
    },
    tooltip: {
      backgroundColor: '#111827',
      borderColor: 'transparent',
      borderRadius: 10,
      shadow: false,
      style: { color: '#f9fafb', fontSize: '12px' },
      pointFormat: '<b>{point.name}</b><br>{point.value} visitors &middot; {point.rateLabel} booked',
    },
    series: [
      {
        // Every country drawn, so the shaded ones read as part of a world map
        // rather than as shapes floating in space.
        name: 'World',
        borderColor: tokens.line,
        borderWidth: 0.4,
        nullColor: tokens.grid,
        showInLegend: false,
        enableMouseTracking: false,
      },
      {
        name: 'Visitors',
        joinBy: ['iso-a3', 'code3'],
        borderColor: tokens.line,
        borderWidth: 0.4,
        states: { hover: { borderColor: PALETTE.blue, brightness: 0.06 } },
        data: props.data.map(c => ({
          code3: c.code3,
          name: c.name,
          value: c.visitors,
          rateLabel: c.conversion_rate == null ? '0%' : `${Math.round(c.conversion_rate * 100)}%`,
        })),
      },
    ],
  }
}, [toRef(props, 'data')])
</script>
